
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ShieldAlert, UserCheck, UserX, FileEdit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface SecurityLog {
  id: string;
  action: string;
  user_id: string;
  user_email: string;
  ip_address: string;
  details: any;
  created_at: string;
}

// Mock data - in a real application, this would come from a security logs table
const generateMockLogs = () => {
  const actions = ['login', 'signup', 'password_reset', 'admin_action', 'profile_update', 'user_deleted'];
  const ips = ['192.168.1.1', '10.0.0.5', '172.16.0.10', '91.234.56.78', '45.67.89.123'];
  
  return Array.from({ length: 15 }, (_, i) => {
    const action = actions[Math.floor(Math.random() * actions.length)];
    const date = new Date();
    date.setHours(date.getHours() - i * 2);
    
    return {
      id: `log-${i}`,
      action,
      user_id: `user-${Math.floor(Math.random() * 100)}`,
      user_email: `user${Math.floor(Math.random() * 100)}@example.com`,
      ip_address: ips[Math.floor(Math.random() * ips.length)],
      details: { 
        browser: 'Chrome',
        os: 'Windows',
        success: Math.random() > 0.2
      },
      created_at: date.toISOString()
    };
  });
};

const SecurityLogsTab = () => {
  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    fetchSecurityLogs();
  }, []);
  
  const fetchSecurityLogs = async () => {
    setLoading(true);
    
    try {
      // In a real app, fetch from your security logs table
      // For demo purposes, we're using mock data
      const mockLogs = generateMockLogs();
      setLogs(mockLogs);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
    } catch (error) {
      console.error('Error fetching security logs:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les journaux de sécurité.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const getActionIcon = (action: string) => {
    switch(action) {
      case 'login':
        return <UserCheck className="h-4 w-4 text-green-500" />;
      case 'signup':
        return <UserCheck className="h-4 w-4 text-blue-500" />;
      case 'password_reset':
        return <ShieldAlert className="h-4 w-4 text-amber-500" />;
      case 'admin_action':
        return <ShieldAlert className="h-4 w-4 text-purple-500" />;
      case 'profile_update':
        return <FileEdit className="h-4 w-4 text-blue-500" />;
      case 'user_deleted':
        return <Trash2 className="h-4 w-4 text-red-500" />;
      default:
        return <ShieldAlert className="h-4 w-4 text-gray-500" />;
    }
  };
  
  const getActionLabel = (action: string) => {
    switch(action) {
      case 'login':
        return <Badge className="bg-green-500">Connexion</Badge>;
      case 'signup':
        return <Badge className="bg-blue-500">Inscription</Badge>;
      case 'password_reset':
        return <Badge className="bg-amber-500">Réinitialisation mot de passe</Badge>;
      case 'admin_action':
        return <Badge className="bg-purple-500">Action administrateur</Badge>;
      case 'profile_update':
        return <Badge className="bg-blue-500">Mise à jour profil</Badge>;
      case 'user_deleted':
        return <Badge className="bg-red-500">Suppression utilisateur</Badge>;
      default:
        return <Badge>{action}</Badge>;
    }
  };
  
  const getFilteredLogs = () => {
    return logs.filter(log => {
      const searchLower = searchTerm.toLowerCase();
      return (
        log.user_email.toLowerCase().includes(searchLower) ||
        log.action.toLowerCase().includes(searchLower) ||
        log.ip_address.includes(searchTerm)
      );
    });
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>Journal de sécurité</CardTitle>
          <CardDescription>Visualisez l'activité et les événements de sécurité de la plateforme</CardDescription>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchSecurityLogs}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-2 mb-6">
          <Input
            placeholder="Rechercher par email, action ou IP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Adresse IP</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredLogs().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      Aucun événement trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredLogs().map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {new Date(log.created_at).toLocaleDateString('fr-FR')} {new Date(log.created_at).toLocaleTimeString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getActionIcon(log.action)}
                          <span>{getActionLabel(log.action)}</span>
                        </div>
                      </TableCell>
                      <TableCell>{log.user_email}</TableCell>
                      <TableCell className="font-mono text-xs">{log.ip_address}</TableCell>
                      <TableCell>
                        {log.details?.success 
                          ? <Badge className="bg-green-500">Succès</Badge>
                          : <Badge className="bg-red-500">Échec</Badge>
                        }
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SecurityLogsTab;
